import {
  RecipeDetailProduct,
  RecipeDetailResponse,
} from '@diamond/shared/types';
import { useMemo, useReducer } from 'react';

type ProductOrder = Array<string>;
type ProductEntries = Record<string, RecipeDetailProduct>;
type HandleChangeProductArg = { index: number; productId: string };

export function useRecipeAddToCart(
  data: RecipeDetailResponse,
  isRecommended: boolean
) {
  const [state, dispatch] = useReducer(reducer, {
    productsOrder: getProductOrdering(data),
    selectedProductOrder: getProductOrdering(data).map((order) =>
      isRecommended ? order[0] : order?.[1] || order?.[0]
    ),
  });

  const productEntries = useMemo(() => getProductEntries(data), [data]);
  const selectedSingleProducts = state.selectedProductOrder.map(
    (id) => productEntries[id]
  );

  const [totalHPP, totalProduct] = selectedSingleProducts.reduce(
    (acc, curr) => {
      if (curr.stock === 0 || curr.stock < curr.minimum_order_quantity)
        return acc;
      return [acc[0] + curr.subtotal, acc[1] + 1];
    },
    [0, 0]
  );

  const handleChangeProduct = ({
    index,
    productId,
  }: HandleChangeProductArg) => {
    dispatch({ type: 'CHANGE_PRODUCT', index, productId });
  };

  const handleDeleteProduct = (index: number) => {
    dispatch({ type: 'DELETE_PRODUCT', index });
  };

  const resetState = () => {
    dispatch({ type: 'RESET_STATE', data });
  };

  return {
    totalHPP,
    totalUnavailableProduct: state.selectedProductOrder.length - totalProduct,
    totalProduct,
    products: state.selectedProductOrder.map((orderId, index) => ({
      order: state.productsOrder[index].map((id) => productEntries[id]),
      selected: state.productsOrder[index].findIndex(
        (productOrderId) => productOrderId === orderId
      ),
    })),
    selectedAvailableProducts: state.selectedProductOrder
      .map((orderId, index) => {
        return productEntries[
          state.productsOrder[index].find((p) => p === orderId) || 0
        ];
      })
      .filter((p) => p.stock > 0 && p.stock >= p.minimum_order_quantity),
    handleChangeProduct,
    handleDeleteProduct,
    resetState,
  };
}

type State = {
  selectedProductOrder: Array<string>;
  productsOrder: Array<ProductOrder>;
};

type ChangeProductAction = {
  type: 'CHANGE_PRODUCT';
  index: number;
  productId: string;
};

type DeleteProductAction = {
  type: 'DELETE_PRODUCT';
  index: number;
};

type ResetAction = {
  type: 'RESET_STATE';
  data: RecipeDetailResponse;
};

type Actions = ChangeProductAction | DeleteProductAction | ResetAction;

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case 'CHANGE_PRODUCT': {
      const newSelectedProductOrder = [...state.selectedProductOrder];
      newSelectedProductOrder[action.index] = action.productId;
      return {
        ...state,
        selectedProductOrder: newSelectedProductOrder,
      };
    }
    case 'DELETE_PRODUCT': {
      return {
        ...state,
        selectedProductOrder: state.selectedProductOrder.filter(
          (_, i) => i !== action.index
        ),
        productsOrder: state.productsOrder.filter((_, i) => i !== action.index),
      };
    }
    case 'RESET_STATE': {
      const newProductsOrder = getProductOrdering(action.data);
      return {
        ...state,
        productsOrder: newProductsOrder,
        selectedProductOrder: newProductsOrder.map((order) => order[0]),
      };
    }
    default:
      return state;
  }
}

/**
 * Construct product entries from ingredient groups
 *
 * Example:
 * {
 *   'product_id_1': { id: "product_id_1", title: "Fresh Milk", ...}
 *   'product_id_2': { id: "product_id_2", title: "Fresh Milk 2", ...}
 * }
 *
 * @param data RecipeDetailResponse
 * @returns ProductEntries
 */
function getProductEntries(data: RecipeDetailResponse) {
  return data.ingredient_groups.reduce<ProductEntries>((acc, group) => {
    const ingredientProducts = group.ingredients.reduce<ProductEntries>(
      (acc, ig) => {
        if (
          ig.recommended_products?.[0] &&
          !acc[ig.recommended_products[0].id]
        ) {
          acc[ig.recommended_products[0].id] = ig.recommended_products[0];
        }

        if (
          ig.alternative_products?.[0] &&
          !acc[ig.alternative_products[0].id]
        ) {
          acc[ig.alternative_products[0].id] = ig.alternative_products[0];
        }

        return acc;
      },
      {}
    );
    return { ...acc, ...ingredientProducts };
  }, {});
}

/**
 * Construct an array of product for ordering.
 * Each item contain a tuple of recommended product id and alternative product id
 *
 * Example:
 * [
 *  ['product_id_1', 'product_id_2'],
 *  ['product_id_3', 'product_id_3']
 * ]
 *
 * @param data RecipeDetailResponse
 * @returns Array<ProductOrder>
 */
function getProductOrdering(data: RecipeDetailResponse): Array<ProductOrder> {
  return data.ingredient_groups.flatMap<ProductOrder>((group) =>
    group.ingredients.reduce<Array<ProductOrder>>((acc, curr) => {
      const item = [];
      if (curr.recommended_products.length > 0) {
        item.push(curr.recommended_products[0].id);
      }
      if (curr.alternative_products.length > 0) {
        item.push(curr.alternative_products[0].id);
      }
      if (item.length > 0) {
        acc.push(item);
      }
      return acc;
    }, [])
  );
}
